import { ANALYTICS_HEAP_IDS, formatHeapId } from '@/common/constants';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { NavbarItemProp } from '../../../types';
import { Expansion } from '../Expansion/Expansion';
import styles from './NavItems.module.scss';

export const NavItems: React.FC<{
  navItems: NavbarItemProp[];
}> = ({ navItems }) => {
  const [expandedItem, setExpandedItem] = useState<NavbarItemProp | null>(null);

  const handleNavItemExpansion = (item: NavbarItemProp | null) => {
    if (!item || expandedItem?.title === item.title) {
      setExpandedItem(null);
      return;
    }
    setExpandedItem(item);
  };

  const handleScroll = useCallback(() => {
    if (expandedItem) {
      setExpandedItem(null);
    }
  }, [expandedItem]);

  // Tabbing out of the expansion should collapse it
  const handleFocusOut = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      if (!expandedItem) return;

      const currentTarget = event.currentTarget;
      const relatedTarget = event.relatedTarget as HTMLElement;

      // Check if focus is moving outside the current target
      if (relatedTarget && !currentTarget.contains(relatedTarget)) {
        // Close expansion when focus leaves (whether to another nav item or elsewhere)
        setExpandedItem(null);
      }
    },
    [expandedItem],
  );

  useEffect(() => {
    // Check if window is available (for SSR compatibility)
    if (typeof window !== 'undefined') {
      // Add scroll event listener
      window.addEventListener('scroll', handleScroll, { passive: true });

      // Cleanup event listener on unmount
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <nav aria-label="Main navigation" className={styles.nav_items}>
      <ul onBlur={handleFocusOut}>
        {navItems.map((item) =>
          item.type === 'NavitemExpansionV2' ? (
            <li
              key={item.title}
              className={classNames(
                expandedItem?.title === item.title ? styles.active_item : undefined,
                styles.nav_item_btn,
              )}
              onMouseEnter={() => handleNavItemExpansion(item)}
              onMouseLeave={() => handleNavItemExpansion(null)}
              onClick={() => handleNavItemExpansion(item)}
              onBlur={handleFocusOut}
            >
              <button
                id={item.title}
                aria-haspopup="true"
                aria-expanded={expandedItem?.title === item.title}
                data-heap-id={formatHeapId([
                  ANALYTICS_HEAP_IDS.DESKTOP,
                  ANALYTICS_HEAP_IDS.NAVBAR,
                  item.title,
                ])}
              >
                {item.title}
              </button>
              <Expansion
                items={item.expansionItems}
                isExpanded={expandedItem?.title === item.title}
                parentTitle={item.title}
              />
            </li>
          ) : (
            <li
              key={item.title}
              className={styles.nav_item_link}
              onMouseEnter={() => handleNavItemExpansion(null)}
              onBlur={handleFocusOut}
            >
              <a
                href={item.expansionItems[0].expansionLinks[0].link}
                data-heap-id={formatHeapId([
                  ANALYTICS_HEAP_IDS.DESKTOP,
                  ANALYTICS_HEAP_IDS.NAVBAR,
                  item.title,
                ])}
              >
                {item.title}
              </a>
            </li>
          ),
        )}
      </ul>
    </nav>
  );
};
